import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { createKeyClickHandler } from '@zola/zola-ui/src/util/handleKeys';
import { ButtonV3 as Button } from '@zola/zola-ui/src/components/ButtonV3';
import _uniqBy from 'lodash/uniqBy';
import _isEqual from 'lodash/isEqual';
import queryString from 'query-string';
import cx from 'classnames';
import { deleteCookie, getCookie } from '@zola-helpers/client/dist/es/util/storage';
import { getModuleGroup } from 'selectors/moduleGroupSelectors';
import { CARD_TYPES_WITH_QR_CODES, CARD_TYPES } from 'cards/constants/CardTypes';
import { getCardSettings } from 'cards/util/cardSettingsUtils';
import NoDesignsAvailable from 'cards/components/CardListing/CardListingRedesign/NoDesignsAvailable/NoDesignsAvailable';
import MultiSample from 'cards/components/common/Samples/MultiSample/MultiSample';
import MinimalLoader from 'components/common/ui/MinimalLoader';
import { DigitalHowItWorksTile } from 'cards/components/CardListing/Ads/Digital';
import QRPLPModule from 'assets/images/invitations/qr-code-plp-marketting-tile.jpg';
import { FloatingActions } from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/FloatingActions';
import { RegistryAdTile } from 'cards/components/CardListing/Ads/RegistryAdTile';
import { getPLPPhotoPreviewState } from 'cards/selectors/plpPhotoPreview';
import ListingDetail from 'cards/components/CardListing/ListingDetail/ListingDetail_next';
import SEOSchema from 'cards/components/CardListing/PaginatedCardContainer/SEOSchema';
import {
  requestInitialCardSuites,
  requestAdditionalCardSuites,
  requestPreviousCardSuites,
  initialLoadMatchingThemeCardsForUser,
  resetThemeMatchedCards,
} from 'cards/actions/cardCatalogActions';
import { setPDPInitialVariationColor } from 'cards/reducers/pdp/actions';
import {
  getCardCatalogBusy,
  getCollaborators,
  getDisplayableSearchTotal,
  getLoadedEndIndex,
  getLoadedStartIndex,
  getThemeMatchedCards,
  getSuiteLeadCards,
  getIsFilteredCardSearch,
  getCardPromotionData,
} from 'cards/selectors/cardCatalogSelector';
import { isPostAuthNav3MobileOrDesktop } from 'util/isPostAuthNav3Active';
import { getActiveThumbnailUrl } from 'paper/utils/helpers';
import { trackProductListViewed } from '@zola/tracking-contracts/src/tracking';
import {
  trackedFilters,
  consolidateFiltersByKey,
  injectDigitalTiles,
  injectPlpRedesignAdTiles,
  TRACKING_BUSINESS_CATEGORIES,
} from 'cards/components/CardListing/util';
import { PlantATreeAdTile } from 'components/common/PlantATree/styles';
import {
  trackProductListViewed as deprecatedTrackProductListViewed,
  trackLoadMorePLP,
} from '../../../../util/trackingHelper';
import featureFlags from '../../../../util/featureFlags';
import Tile from '../Ads/Tile';
import { MagicAdTile } from '../Ads/MagicAdTile';
import { PromoTileCarousel } from '../Ads/PromoTileCarousel/PromoTileCarousel';

import paginatedStyles from './PaginatedCardContainer.module.less';
import styles from './styles.module.less';
import { openModal } from '../../../actions/modalActions';

const withoutAdsOrDigital = tile => tile.type !== 'Ad' && tile.type !== 'Digital';
const withoutQRModule = tile => tile.type !== 'qrModule';

/**
 * Renders an add tile
 * @param {Object} props - Properties
 * @param {string[]} props.tiles Tiles to be used in the form of urls
 * @param {number} props.tileCount Amount of tiles rendered
 * @param {() => void} props.onBeforeRender Callback that happens before returning the JSX
 * @returns {Tile}
 */
const renderAdTile = ({ tiles, tileCount, isSale = false }) => {
  const idx = tileCount;
  const len = tiles.length;
  const module = tiles[((idx % len) + len) % len];
  return <Tile module={module} position={idx + 1} isSale={isSale} />;
};

class PaginatedCardContainerOriginal extends Component {
  constructor(props) {
    super(props);
    const { loadedStartIndex, loadedEndIndex, searchTotal } = props;
    this.timer = null;
    this.state = {
      startIndex: loadedStartIndex,
      endIndex: Math.min(loadedEndIndex, searchTotal),
      trackingFired: false,
      isPostAuthNav3: false,
    };
  }

  componentDidMount() {
    const { loadedStartIndex, loadThemeMatchingCards, userContext } = this.props;
    if (loadedStartIndex !== 0) {
      this.preFetchPreviousPage();
    }
    if (typeof window !== 'undefined') {
      loadThemeMatchingCards();
      this.preFetchNextPage();
    }

    const newUserPaperFavoriteKeyForScroll = getCookie('newUserPaperFavoriteKeyForScroll');
    if (newUserPaperFavoriteKeyForScroll) {
      const productTile = document.getElementById(newUserPaperFavoriteKeyForScroll);
      if (productTile) {
        productTile.scrollIntoView({ block: 'center' });
      }
      deleteCookie('newUserPaperFavoriteKeyForScroll');
    }
    this.setState({
      isPostAuthNav3: isPostAuthNav3MobileOrDesktop(userContext),
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { startIndex, endIndex } = this.state;
    const { searchFilters, activeCardEvent, plpPhotoPreviewData } = this.props;
    if (startIndex !== prevState.startIndex) this.preFetchPreviousPage();
    if (endIndex !== prevState.endIndex) this.preFetchNextPage();

    const filtersChanged = !_isEqual(searchFilters, prevProps.searchFilters);
    const cardTypeChanged = activeCardEvent !== prevProps.activeCardEvent;
    if ((filtersChanged || cardTypeChanged) && startIndex !== null) {
      this.handleLoadInitialSuites(0);
    }

    if (plpPhotoPreviewData !== prevProps.plpPhotoPreviewData) {
      this.handleTrackProductListViewed();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  onClickListingDetail = searchIndex => {
    const { cardsPerPage, isMobile } = this.props;
    // For mobile start at the card clicked.  For desktop, the current page.
    const saveIndex = isMobile
      ? searchIndex
      : Math.floor(searchIndex / cardsPerPage) * cardsPerPage;
    browserHistory.replace({
      pathname: window.location.pathname,
      query: {},
      state: { index: saveIndex },
    });
    const offset = isMobile ? searchIndex : Math.floor(searchIndex / cardsPerPage) * cardsPerPage;
    // eslint-disable-next-line no-restricted-globals
    if (!isNaN(offset)) {
      const parsedQueryString = queryString.parse(window.location.search);
      const newQueryString = queryString.stringify(
        { ...parsedQueryString, offset },
        { skipEmptyString: true }
      );

      browserHistory.push({
        pathname: browserHistory.getCurrentLocation().pathname,
        search: `?${newQueryString}`,
      });
    }
  };

  onShowMore = () => {
    const { endIndex } = this.state;
    const { cardsPerPage, loadedEndIndex } = this.props;
    const newEndIndex = Math.min(endIndex + cardsPerPage, loadedEndIndex);
    this.setState({
      endIndex: newEndIndex,
    });
    trackLoadMorePLP();
  };

  onShowPrevious = () => {
    const { startIndex } = this.state;
    const { cardsPerPage, loadedStartIndex } = this.props;
    const newStartIndex = Math.max(0, startIndex + 1 - cardsPerPage, loadedStartIndex);
    this.setState({
      startIndex: newStartIndex,
    });
  };

  getDisplayIndexes = () => {
    const { startIndex, endIndex } = this.state;
    const {
      loadedStartIndex,
      loadedEndIndex,
      searchTotal,
      cardsPerPage,
      relatedThemeLeadCards,
      searchFilters,
      ads,
      hasFilters,
      allowMultiSample,
      isDigital,
      isMobile,
    } = this.props;

    const numFilteredCardsShowing = endIndex + 1 - startIndex;
    const numPages = Math.ceil(numFilteredCardsShowing / cardsPerPage);

    const mockFilteredCards = [...Array(searchTotal)].map((_, index) => {
      const newVal = {};
      if (index === loadedStartIndex) newVal.loadedStartIndex = true;
      if (index === startIndex) newVal.startIndex = true;
      if (index === endIndex) newVal.endIndex = true;
      if (index === loadedEndIndex) newVal.loadedEndIndex = true;
      return newVal;
    });
    // Insert Themes
    const numThemeCards = relatedThemeLeadCards.length;
    const shouldIncludeThemeCards = !hasFilters && !searchFilters.collaborator && startIndex === 0;
    const mockThemeCards = [...Array(numThemeCards)].map(() => ({}));
    const finalCardList = shouldIncludeThemeCards
      ? mockThemeCards.concat(mockFilteredCards)
      : mockFilteredCards;

    function getAdjustedIndex(index) {
      if (numThemeCards === 0) return index;
      return shouldIncludeThemeCards && index - numThemeCards > -1 ? index - numThemeCards : index;
    }

    // Insert Ads
    let nextMockFinalCardList;
    if (isDigital) {
      nextMockFinalCardList = injectDigitalTiles(finalCardList);
    } else if (ads.length > 0 && startIndex === 0 && finalCardList.length > 0) {
      nextMockFinalCardList = injectPlpRedesignAdTiles(finalCardList, isMobile);
    } else {
      nextMockFinalCardList = finalCardList;
    }
    let displayLoadedStartIndex;
    let displayStartIndex;
    const displayCardTotal = nextMockFinalCardList.length;
    nextMockFinalCardList.forEach((val, index) => {
      // Adjust the indexes here to account for the theme cards.
      if (val && val.loadedStartIndex) displayLoadedStartIndex = getAdjustedIndex(index);
      if (val && val.startIndex) displayStartIndex = getAdjustedIndex(index);
    });
    // Derive the display end index from the start index and cards showing.
    let displayEndIndex = displayStartIndex + numPages * cardsPerPage - 1;
    if (endIndex + 1 === searchTotal) displayEndIndex = displayCardTotal;
    if (allowMultiSample) displayEndIndex -= 1;

    // Calculate the number of cards on the next page based on the last displayed index
    const remainingCards = nextMockFinalCardList
      .slice(displayEndIndex + 1)
      .filter(withoutAdsOrDigital)
      .filter(withoutQRModule);
    const nextPageCount = Math.max(0, Math.min(cardsPerPage, remainingCards.length));

    return {
      displayLoadedStartIndex,
      displayStartIndex,
      displayEndIndex,
      displayCardTotal,
      nextPageCount,
    };
  };

  setInitialIndexState = () => {
    const { loadedStartIndex, loadedEndIndex, searchTotal } = this.props;
    this.setState({
      startIndex: loadedStartIndex,
      endIndex: Math.min(loadedEndIndex, searchTotal),
    });
  };

  clearHistoryState = () => {
    browserHistory.replace({
      pathname: window.location.pathname,
      query: {},
      state: { index: 0 },
    });
  };

  createFinalCardList = () => {
    const { startIndex } = this.state;
    const {
      searchFilters,
      filteredSuiteLeadCards,
      ads,
      relatedThemeLeadCards,
      hasFilters,
      isDigital,
      isMobile,
    } = this.props;
    // Build the cards to display here.
    const showThemeCards = !hasFilters && !searchFilters.collaborator && startIndex === 0;
    // Theme cards first.
    const cards = showThemeCards ? [...relatedThemeLeadCards] : [];
    // Then add the filtered cards.
    filteredSuiteLeadCards.forEach(card => cards.push(card));
    const uniqueCards = _uniqBy(cards, 'uuid');
    // Inject ads last.

    if (isDigital) {
      return injectDigitalTiles(uniqueCards);
    }
    // To keep ads in proper position, only inject them when start index is 0.
    if (ads.length > 0 && startIndex === 0 && uniqueCards.length > 0) {
      return injectPlpRedesignAdTiles(uniqueCards, isMobile);
    }
    return uniqueCards;
  };

  handleLoadInitialSuites = initialOffset => {
    const {
      searchFilters,
      loadThemeMatchingCards,
      fetchInitialCardSuites,
      cardsPerPage,
      hasFilters,
    } = this.props;

    this.resetIndexes();

    const shouldIncludeThemeCards = !hasFilters && !searchFilters.collaborator;
    const relatedThemesCardsPromise = shouldIncludeThemeCards
      ? loadThemeMatchingCards()
      : Promise.resolve();

    const filteredCardsPromise = fetchInitialCardSuites(initialOffset, cardsPerPage).then(() =>
      this.setInitialIndexState()
    );

    Promise.all([filteredCardsPromise, relatedThemesCardsPromise]).then(() => {
      this.handleTrackProductListViewed(true);
    });
  };

  handleTrackProductListViewed = (fireEventOnDemand = false) => {
    const {
      cardType,
      searchFilters,
      plpPhotoPreviewData: { isInUseForCardType: isCustomPhotoPreviewInUse, currentFlowUuid },
      isDigital,
    } = this.props;
    const { trackingFired } = this.state;
    const trackedSort = [
      {
        type: 'SORT',
        value: searchFilters?.sorts?.[0]?.toUpperCase() || 'FEATURED',
      },
    ];
    const fullCardList = this.createFinalCardList();
    const {
      displayStartIndex,
      displayEndIndex,
      displayLoadedStartIndex,
    } = this.getDisplayIndexes();

    const products = fullCardList.reduce((acc, card, index) => {
      const normalizedIndex = index + displayLoadedStartIndex;
      const show = normalizedIndex >= displayStartIndex && normalizedIndex <= displayEndIndex;
      const isCardProduct =
        card.type !== 'sample' &&
        card.type !== 'Ad' &&
        card.type !== 'Digital' &&
        card.type !== 'qrModule';
      const isFirstTwelve = normalizedIndex < displayStartIndex + 12;
      if (show && isCardProduct && isFirstTwelve) {
        const {
          uuid,
          colors,
          suiteUUID,
          type,
          name,
          suiteName,
          key,
          min_price_cents: minPriceCents,
        } = card;
        const { urlSlug } = getCardSettings(type);
        const imageUrl = getActiveThumbnailUrl(colors?.[0], searchFilters);
        const cardTypeMapped = TRACKING_BUSINESS_CATEGORIES[type] || type;

        // Temp until we fully turn on the Tracking contracts
        let productTrackingDataObject = {
          product_id: suiteUUID,
          sku_id: uuid,
          category: type,
          name,
          brand: suiteName,
          variant: key,
          price: (minPriceCents / 100).toFixed(2),
          quantity: 1,
          position: index,
          url: `/wedding-planning/${urlSlug}/${suiteUUID}`,
          image_url: imageUrl || 'N/A',
        };

        if (featureFlags.get('updateSegmentEventsPLV')) {
          productTrackingDataObject = {
            card_suite_uuid: suiteUUID,
            sku: uuid,
            category: isDigital ? `DIGITAL_${cardTypeMapped}` : cardTypeMapped,
            name,
            brand: suiteName,
            price: +(minPriceCents / 100).toFixed(2),
            quantity: 1,
            image_url: imageUrl || 'N/A',
            position: index,
          };
        }
        acc.push(productTrackingDataObject);
      }
      return acc;
    }, []);

    const eventProperties = {
      category: 'Paper',
      filters: trackedFilters(searchFilters),
      sorts: searchFilters?.sorts?.length > 0 ? searchFilters.sorts : ['featured'],
      products,
      // in case of custom photo preview
      ...(currentFlowUuid ? { flow_run_id: currentFlowUuid } : {}),
    };

    if (
      (((isCustomPhotoPreviewInUse && currentFlowUuid) || !isCustomPhotoPreviewInUse) &&
        !trackingFired) ||
      fireEventOnDemand
    ) {
      this.setState({
        trackingFired: true,
      });

      if (featureFlags.get('updateSegmentEventsPLV')) {
        const cardTypeMapped = TRACKING_BUSINESS_CATEGORIES[cardType] || cardType;
        const newEventProperties = {
          business_unit: 'PAPER',
          business_category: isDigital ? `DIGITAL_${cardTypeMapped}` : cardTypeMapped,
          entity_type: 'PAPER',
          filters: trackedFilters(consolidateFiltersByKey(searchFilters)),
          sorts: trackedSort,
          products,
        };
        trackProductListViewed(newEventProperties);
      } else {
        deprecatedTrackProductListViewed(eventProperties);
      }
    }
  };

  preFetchNextPage = () => {
    const { endIndex } = this.state;
    const { busy, cardsPerPage, searchTotal, fetchMoreCards, loadedEndIndex } = this.props;
    const readyToSearch = !busy;
    const hasMore = loadedEndIndex + 1 < searchTotal;
    const enoughCardsForNextPage = endIndex + cardsPerPage <= loadedEndIndex;
    const shouldFetchNext = hasMore && !enoughCardsForNextPage && readyToSearch;
    if (shouldFetchNext) {
      fetchMoreCards(loadedEndIndex, cardsPerPage);
    }
  };

  preFetchPreviousPage = () => {
    const { startIndex } = this.state;
    const { busy, cardsPerPage, fetchPreviousCards, loadedStartIndex } = this.props;
    const readyToSearch = startIndex > 0 && !busy;
    const hasPrevious = loadedStartIndex > 0;
    const enoughCardsForPreviousPage = startIndex - cardsPerPage >= loadedStartIndex;
    const shouldFetchPrevious = hasPrevious && !enoughCardsForPreviousPage && readyToSearch;
    if (shouldFetchPrevious) {
      const offset = Math.max(loadedStartIndex - cardsPerPage, 0);
      const limit = Math.min(startIndex, cardsPerPage);
      fetchPreviousCards(offset, limit);
    }
  };

  resetIndexes = () => {
    this.setState({
      startIndex: null,
      endIndex: null,
    });
  };

  renderSpotlightTile = () => {
    const {
      saleTile,
      allowMultiSample,
      cardType,
      isDigital,
      isMobile,
      isDesktop,
      showModal,
      promoModuleGroup,
    } = this.props;
    /*
     * Digital Save The Date
     * The first DSTD grid spot is reserved for a "How it works" tile
     * This is an exception amongs card types
     * DSTD is not - for now - compatible with the next conditions
     */
    if (isDigital) {
      return (
        <div
          className={cx('card-listing__v2', {
            twoColSpanTile: isDesktop,
          })}
          key="how-digital-works-ad-tile"
        >
          <DigitalHowItWorksTile />
        </div>
      );
    }

    // * For all other card types, the spotlight tile is the 1st row's last item
    // * Conditions are ordered by precedence (P)
    const spotlightTileClasses = 'card-listing__v2 spotlight-tile';

    // * P0 - Promo carousel tile
    const showPLPPromoPlacement = promoModuleGroup && promoModuleGroup.moduleViews?.length > 0;
    if (showPLPPromoPlacement) {
      return (
        <div className={cx(spotlightTileClasses, 'no-padding')} key="promo-tile-carousel">
          <PromoTileCarousel promoModuleGroup={promoModuleGroup} />
        </div>
      );
    }

    // * P1 - Sale tile
    const hasSaleTile = !!saleTile?.length;
    if (hasSaleTile) {
      return (
        <div className={spotlightTileClasses} key="sale-ad-tile">
          {renderAdTile({
            tiles: saleTile,
            tileCount: 0 - 1,
            isSale: true,
          })}
        </div>
      );
    }

    // * P2 - Card types eligible to QR codes
    const showQRTile = !isMobile && CARD_TYPES_WITH_QR_CODES.includes(cardType);
    if (showQRTile) {
      const openQrModal = () => showModal('QR_CODE_INFO', {}, {});
      return (
        <div
          className={cx(spotlightTileClasses, styles.qrPLPModule)}
          onClick={openQrModal}
          onKeyDown={createKeyClickHandler(openQrModal)}
          key="qr-ad-tile"
        >
          <img
            className={styles.adTileImg}
            src={QRPLPModule}
            alt="Add a QR code to share info in a snap"
          />
        </div>
      );
    }

    if (allowMultiSample) {
      // * P3 - Card types eligible to Samples
      return (
        <div className={spotlightTileClasses} key="multisample-ad-tile">
          <MultiSample.PLPTile cardType={cardType} isDesktop={isDesktop} />
        </div>
      );
    }

    return null;
  };

  renderCards = ({
    displayEndIndex,
    displayLoadedStartIndex,
    displayStartIndex,
    finalCardList,
  }) => {
    const {
      searchFilters,
      ads,
      activeCardEvent,
      basePlanningUrl,
      collaborators,
      onSetInitialVariationColor,
      allowMultiSample,
      cardType,
      isMobile,
      isDesktop,
      userContext,
      promotionData,
    } = this.props;
    let adTileCount = -1;

    return finalCardList.map((card, index) => {
      // Adjust for previous cards that have not been loaded.
      const normalizedIndex = index + displayLoadedStartIndex;
      const show = normalizedIndex >= displayStartIndex && normalizedIndex <= displayEndIndex;

      if (!show) return null;

      /*
       * Evergreen marketing modules
       * Mix of hardcoded and admin module controlled ones
       * Starting at n+4 in the product grid
       */
      if (card.type === 'Ad') {
        adTileCount += 1;

        /*
         * Render Disney tile for the first Ad position (n+4)
         * Not applicable to Shower cards or Table numbers
         */
        if (
          !featureFlags.get('plantATree') &&
          adTileCount === 0 &&
          userContext?.user_experiments &&
          cardType !== CARD_TYPES.weddingShower &&
          cardType !== CARD_TYPES.tableNumber
        ) {
          return (
            <div className="card-listing__v2" key="magic-ad-tile">
              <MagicAdTile cardType={cardType} />
            </div>
          );
        }

        if (featureFlags.get('plantATree') && adTileCount === 0) {
          return (
            <div className="card-listing__v2" key="plant-a-tree-ad-tile">
              <PlantATreeAdTile
                href="https://www.zola.com/faq/plus-one"
                target="_blank"
                title="The +1 pledge"
              />
            </div>
          );
        }

        /*
         * Render a Registry cross sell tile
         * Third Ad position
         */
        if (adTileCount === 2 && [CARD_TYPES.std, CARD_TYPES.invitation].includes(cardType)) {
          return (
            <div className="card-listing__v2" key="registry-ad-tile">
              <RegistryAdTile />
            </div>
          );
        }

        /*
         * Render the evergreen marketing tiles
         * Controlled from admin modules
         */
        return (
          <div className="card-listing__v2" key={`${card.uuid}-ad-tile`}>
            {renderAdTile({
              tiles: ads,
              tileCount: adTileCount - 1,
            })}
          </div>
        );
      }

      /*
       * Special DSTD "How it works" tiles (that replace ad tiles)
       * Inject Double sized How to module based on the digital type
       * defined previously (n+11+)
       */
      if (card.type === 'Digital') {
        return (
          <div
            className={cx('card-listing__v2', {
              twoColSpanTile: isDesktop,
            })}
            key={card.uuid}
          >
            <DigitalHowItWorksTile />
          </div>
        );
      }

      /*
       * Card tile rendering
       */
      return (
        <div
          className={cx('card-listing__v2', paginatedStyles['card-listing__mobile'])}
          key={card.uuid}
        >
          <ListingDetail
            tileIndex={index}
            activeCardEvent={activeCardEvent}
            basePlanningUrl={basePlanningUrl}
            card={card}
            collaborator={collaborators[card.collaborator]}
            filterColor={searchFilters.color}
            filterMultiColor={searchFilters.multiColor}
            onSetInitialVariationColor={onSetInitialVariationColor}
            plpOnClickCallback={() => this.onClickListingDetail(card.searchIndex)}
            allowMultiSample={allowMultiSample}
            searchFilters={searchFilters}
            isMobile={isMobile}
            promotionData={promotionData}
          />
        </div>
      );
    });
  };

  render() {
    const {
      busy,
      clearFilters,
      searchTotal,
      isDigital,
      basePlanningUrl,
      isMobile,
      cardType,
      handleFilterUpdate,
    } = this.props;
    const { isPostAuthNav3 } = this.state;
    const finalCardList = this.createFinalCardList();
    const {
      displayLoadedStartIndex,
      displayStartIndex,
      displayEndIndex,
      displayCardTotal,
      nextPageCount,
    } = this.getDisplayIndexes();
    const showLoadPrevious = displayStartIndex > 0;
    const showLoadMoreButton = isDigital
      ? finalCardList.length !== 0 &&
        finalCardList.length + 2 > 0 &&
        !(displayEndIndex + 1 >= displayCardTotal)
      : finalCardList.length > 0 && !(displayEndIndex + 1 >= displayCardTotal);
    const displayedTiles = finalCardList.slice(displayStartIndex, displayEndIndex + 1);
    const displayedSuites = displayedTiles.filter(withoutAdsOrDigital).filter(withoutQRModule);
    const localDisplayEndIndex = isDigital ? displayEndIndex + 2 : displayEndIndex;

    return (
      <Fragment>
        <SEOSchema
          products={displayedSuites}
          baseUrl={basePlanningUrl}
          cardType={cardType}
          isDigital={isDigital}
        />
        <div
          className={cx(paginatedStyles.paginatedCardsContainer, {
            [paginatedStyles['paginatedCardsContainer--isPostAuthNav3']]: isPostAuthNav3,
          })}
        >
          {showLoadPrevious && (
            <div className="card-listing__load-previous--container">
              <Button
                variant="primary"
                onClick={this.onShowPrevious}
                className="card-listing__pagination-btn"
                disabled={busy}
              >
                Load Previous{busy ? <div className="btn-loader" /> : ''}
              </Button>
            </div>
          )}
          <div
            className={cx(styles.loaderAnchor, {
              [styles.loaderAnchorNoGrid]: !searchTotal && !busy,
            })}
          >
            {!finalCardList.length && busy && (
              <div className={styles.loaderContainer}>
                <MinimalLoader />
              </div>
            )}
            {!searchTotal && !busy && <NoDesignsAvailable clearAll={clearFilters} />}
            {!!finalCardList.length && displayStartIndex === 0 && this.renderSpotlightTile()}
            {this.renderCards({
              displayEndIndex: localDisplayEndIndex,
              displayLoadedStartIndex,
              displayStartIndex,
              finalCardList,
            })}
            {finalCardList.length > 0 && !busy && (
              <FloatingActions updateFilter={handleFilterUpdate} />
            )}
          </div>
          {showLoadMoreButton && (
            <div className="card-listing__pagination-btn--show-more">
              <Button
                id="plp-load-more-button"
                fullWidth={isMobile}
                variant="primary"
                onClick={() => this.onShowMore()}
                className={cx('card-listing__pagination-btn', {
                  [styles.digitalLoadMore]: isDigital,
                })}
                disabled={busy || displayEndIndex + 1 >= displayCardTotal || !finalCardList.length}
              >
                {nextPageCount ? `Show Next ${nextPageCount} Designs` : 'Show More'}
                {busy && <div className="btn-loader" />}
              </Button>
            </div>
          )}
        </div>
      </Fragment>
    );
  }
}

PaginatedCardContainerOriginal.propTypes = {
  filteredSuiteLeadCards: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchMoreCards: PropTypes.func.isRequired,
  cardsPerPage: PropTypes.number.isRequired,
  busy: PropTypes.bool.isRequired,
  fetchInitialCardSuites: PropTypes.func,
  handleFilterUpdate: PropTypes.func,
  hasFilters: PropTypes.bool,
  allowMultiSample: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => ({
  busy: getCardCatalogBusy(state),
  collaborators: getCollaborators(state),
  searchTotal: getDisplayableSearchTotal(state),
  relatedThemeLeadCards: getThemeMatchedCards(state),
  filteredSuiteLeadCards: getSuiteLeadCards(state),
  promotionData: getCardPromotionData(state),
  loadedStartIndex: getLoadedStartIndex(state),
  loadedEndIndex: getLoadedEndIndex(state),
  hasFilters: getIsFilteredCardSearch(state),
  plpPhotoPreviewState: getPLPPhotoPreviewState(state),
  promoModuleGroup: getModuleGroup(state, { tag: ownProps.promoTag }),
});

const mapDispatchToProps = dispatch => ({
  onSetInitialVariationColor: color => {
    dispatch(setPDPInitialVariationColor(color));
  },
  showModal: (...args) => dispatch(openModal(...args)),
  loadThemeMatchingCards: async () => {
    try {
      dispatch(resetThemeMatchedCards());
      dispatch(initialLoadMatchingThemeCardsForUser());
    } catch (e) {
      // handle error here
    }
    return null;
  },
  fetchInitialCardSuites: (offset, limit) => dispatch(requestInitialCardSuites(offset, limit)),
  fetchPreviousCards: (offset, limit) => dispatch(requestPreviousCardSuites(offset, limit)),
  fetchMoreCards: (offset, limit) => dispatch(requestAdditionalCardSuites(offset, limit)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaginatedCardContainerOriginal);
